<template>
  <div class="repair-force-finish-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('repairForceFinishManage.page.title')"
      :subtitle="$t('repairForceFinishManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.repairType" clearable class="w-full"
            :placeholder="$t('repairForceFinishManage.repairTypePlaceholder')">
            <el-option v-for="item in repairSettings" :key="item.repairType" :label="item.repairTypeName"
              :value="item.repairType" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.repairName" clearable class="w-full"
            :placeholder="$t('repairForceFinishManage.repairPersonPlaceholder')" @keyup.enter.native="queryRepairPool" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.tel" clearable class="w-full"
            :placeholder="$t('repairForceFinishManage.repairPhonePlaceholder')" @keyup.enter.native="queryRepairPool" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryRepairPool">
          {{ $t('repairForceFinishManage.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetRepairPool">
          {{ $t('repairForceFinishManage.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="listRepairPools">
        <template #left>
          <span class="ds-panel__title">{{ searchForm.roomName }} {{ $t('repairForceFinishManage.forceFinish') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="repairPools" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('repairForceFinishManage.page.empty')" @retry="listRepairPools">
        <el-table-column prop="repairId" :label="$t('repairForceFinishManage.repairId')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.repairId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="repairObjName" :label="$t('repairForceFinishManage.location')" align="center" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.repairObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairTypeName" :label="$t('repairForceFinishManage.repairType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.repairTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairName" :label="$t('repairForceFinishManage.repairPerson')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.repairName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="tel" :label="$t('repairForceFinishManage.contact')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appointmentTime" :label="$t('repairForceFinishManage.appointmentTime')" align="center"
          width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appointmentTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('repairForceFinishManage.submitTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('repairForceFinishManage.status')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('repairForceFinishManage.operation')" align="center" fixed="right" width="180">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="openForceFinishRepair(scope.row)">
              {{ $t('repairForceFinishManage.forceFinish') }}
            </el-button>
            <el-button size="mini" @click="openRepairDetail(scope.row)">
              {{ $t('repairForceFinishManage.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <div class="ds-text-muted">{{ $t('repairForceFinishManage.description') }}</div>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 强制回单弹窗 -->
    <force-finish-repair ref="forceFinishDialog" :visible.sync="forceFinishVisible" @success="handleForceFinishSuccess" />
  </div>
</template>

<script>
import {
  listRepairSettings,
  listOwnerRepairs,
  getDict
} from '@/api/work/repairForceFinishManageApi'
import ForceFinishRepair from '@/components/work/forceFinishRepair'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'RepairForceFinishManageList',
  components: {
    ForceFinishRepair,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        repairType: '',
        repairName: '',
        tel: '',
        roomId: '',
        roomName: '',
        state: '1100'
      },
      repairPools: [],
      repairSettings: [],
      states: [],
      forceFinishVisible: false,
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  mounted() {
    this.loadRepairSettings()
    this.loadDictData()
    this.listRepairPools()
  },
  methods: {
    async loadRepairSettings() {
      try {
        this.repairSettings = await listRepairSettings()
      } catch (error) {
        console.error('获取报修设置失败:', error)
        this.$message.error(this.$t('repairForceFinishManage.page.fetchSettingsError'))
      }
    },
    async loadDictData() {
      try {
        this.states = await getDict('r_repair_pool', 'state')
      } catch (error) {
        console.error('获取状态字典失败:', error)
        this.$message.error(this.$t('repairForceFinishManage.page.fetchDictError'))
      }
    },
    async listRepairPools() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }

        // 清理空值
        Object.keys(params).forEach(key => {
          if (params[key] === '') delete params[key]
        })

        const { data, total } = await listOwnerRepairs(params)
        this.repairPools = data
        this.pagination.total = total
      } catch (error) {
        console.error('获取报修列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('repairForceFinishManage.page.fetchError')
      } finally {
        this.loading = false
      }
    },
    queryRepairPool() {
      this.pagination.current = 1
      this.listRepairPools()
    },
    resetRepairPool() {
      this.searchForm = {
        repairType: '',
        repairName: '',
        tel: '',
        state: '1100'
      }
      this.queryRepairPool()
    },
    openForceFinishRepair(repair) {
      this.forceFinishVisible = true
      this.$nextTick(() => {
        this.$refs.forceFinishDialog.open(repair)
      })
    },
    openRepairDetail(repair) {
      this.$router.push(`/views/work/repairDetail?repairId=${repair.repairId}`)
    },
    handleForceFinishSuccess() {
      this.listRepairPools()
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.listRepairPools()
    },
    handleCurrentChange(current) {
      this.pagination.current = current
      this.listRepairPools()
    }
  }
}
</script>

<style lang="scss" scoped>
.repair-force-finish-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
